import { ExternalLink, Merge, Split, X } from "lucide-mui";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Tab from "@mui/material/Tab";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import toast from "react-hot-toast";
import FormattedDate from "../components/FormattedDate.tsx";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client";
import type {
  MergeSuggestion,
  MergeSuggestionCompanySummary,
} from "../api/client";
import ConfirmDialog from "../components/ConfirmDialog.tsx";
import CountryName from "../components/CountryName";
import FilterBar from "../components/filters/FilterBar.tsx";
import ScopeToggle from "../components/filters/ScopeToggle.tsx";
import type { FilterSpec } from "../components/filters/specs.ts";
import { useFilters } from "../components/filters/useFilters.ts";
import IdentityCell from "../components/IdentityCell.tsx";
import PageHeader from "../components/PageHeader.tsx";
import RowActions from "../components/RowActions.tsx";
import EmptyTableRow from "../components/table/EmptyTableRow.tsx";
import ScrollTable from "../components/table/ScrollTable.tsx";
import TableCard from "../components/table/TableCard.tsx";
import UrlTablePagination from "../components/table/UrlTablePagination.tsx";
import { useTenant } from "../contexts/TenantContext.tsx";
import { useUrlPaging, useUrlState } from "../hooks/useUrlState.ts";
import { formatNumber, humanize } from "../utils/format.ts";
import { similarityColor } from "../utils/statusColors.ts";
import BulkActionBar from "../components/BulkActionBar.tsx";

const STATUS_TABS = ["pending", "approved", "rejected"] as const;
type Status = (typeof STATUS_TABS)[number];
type SuggestionType = "merge" | "unmerge";
// Merge / Unmerge is a slice of the list, not a filter: the bar holds only its toggle.
const NO_FILTERS: FilterSpec[] = [];

const NO_SELECTION = new Set<string>();

function CompanyCell({ company }: { company: MergeSuggestionCompanySummary | null }) {
  const { tenantPath } = useTenant();
  if (!company) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Deleted
      </Typography>
    );
  }
  const facts = [
    company.domain ? <span key="domain">{company.domain}</span> : null,
    company.country ? <CountryName key="country" code={company.country} /> : null,
    company.org_number ? <span key="org">{company.org_number}</span> : null,
  ]
    .filter(Boolean)
    .reduce<ReactNode[]>((acc, node, i) => {
      if (i > 0) acc.push(<span key={`sep-${i}`}>·</span>);
      acc.push(node);
      return acc;
    }, []);
  return (
    <IdentityCell
      name={company.name}
      to={tenantPath(`/companies/${company.id}`)}
      caption={
        facts.length > 0 ? (
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            {facts}
          </Box>
        ) : undefined
      }
    />
  );
}

function SuspectRecords({ context }: { context: Record<string, unknown> | null }) {
  if (!context) return null;
  const records = context.suspect_records as
    | Array<{ source: string; match_confidence: number | null; name: string | null }>
    | undefined;
  if (!records?.length) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      {records.map((r, i) => (
        <Typography key={i} variant="body2" sx={{
          color: "text.secondary"
        }}>
          {r.source}: {r.name ?? "?"}{" "}
          {r.match_confidence != null && `(${(r.match_confidence * 100).toFixed(0)}%)`}
        </Typography>
      ))}
    </Box>
  );
}

export default function MergeSuggestionsPage() {
  const { tenantPath } = useTenant();
  const url = useUrlState();
  const { searchParams } = url;
  const suggestionType: SuggestionType = searchParams.get("type") === "unmerge" ? "unmerge" : "merge";
  const statusParam = searchParams.get("status");
  const status: Status = STATUS_TABS.find((s) => s === statusParam) ?? "pending";
  const paging = useUrlPaging(url);
  const filters = useFilters(NO_FILTERS, { state: url });
  const queryClient = useQueryClient();

  // The selection belongs to the rows on screen: another tab, type or page starts empty.
  const view = `${suggestionType}|${status}|${paging.page}|${paging.rowsPerPage}`;
  const [selection, setSelection] = useState<{ view: string; ids: Set<string> }>({
    view,
    ids: NO_SELECTION,
  });
  const selected = selection.view === view ? selection.ids : NO_SELECTION;
  const setSelected = (ids: Set<string>) => setSelection({ view, ids });
  const [confirmBulkReject, setConfirmBulkReject] = useState(false);

  const statsQuery = useQuery({
    queryKey: keys.mergeSuggestion.stats(),
    queryFn: api.getMergeSuggestionStats,
  });

  const listQuery = useQuery({
    queryKey: keys.mergeSuggestion.list(suggestionType, status, paging.page, paging.rowsPerPage),
    queryFn: () =>
      api.getMergeSuggestions({
        status,
        suggestion_type: suggestionType,
        limit: paging.rowsPerPage,
        offset: paging.offset,
      }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: keys.mergeSuggestion.lists() });
    queryClient.invalidateQueries({ queryKey: keys.mergeSuggestion.stats() });
  };

  const isMerge = suggestionType === "merge";

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.approveMergeSuggestion(id),
    onSuccess: () => {
      invalidate();
      toast.success(isMerge ? "Companies merged" : "Marked as a bad merge");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => api.rejectMergeSuggestion(id),
    onSuccess: () => {
      invalidate();
      toast.success("Suggestion rejected");
    },
  });

  const bulkApproveMutation = useMutation({
    mutationFn: (ids: string[]) => api.bulkApproveMergeSuggestions(ids),
    onSuccess: (_, ids) => {
      setSelected(NO_SELECTION);
      invalidate();
      toast.success(`${formatNumber(ids.length)} ${ids.length === 1 ? "suggestion" : "suggestions"} approved`);
    },
  });

  const bulkRejectMutation = useMutation({
    mutationFn: (ids: string[]) => api.bulkRejectMergeSuggestions(ids),
    onSuccess: (_, ids) => {
      setSelected(NO_SELECTION);
      setConfirmBulkReject(false);
      invalidate();
      toast.success(`${formatNumber(ids.length)} ${ids.length === 1 ? "suggestion" : "suggestions"} rejected`);
    },
  });

  const stats = statsQuery.data;
  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;

  const pendingCount = (isMerge ? stats?.merge_pending : stats?.unmerge_pending) ?? 0;
  const approvedCount = (isMerge ? stats?.merge_approved : stats?.unmerge_approved) ?? 0;
  const rejectedCount = (isMerge ? stats?.merge_rejected : stats?.unmerge_rejected) ?? 0;

  const isPending = status === "pending";
  const allSelected = isPending && items.length > 0 && items.every((s) => selected.has(s.id));
  const someSelected = isPending && items.some((s) => selected.has(s.id));
  const bulkPending = bulkApproveMutation.isPending || bulkRejectMutation.isPending;
  const colSpan = isPending ? 7 : 5;

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(NO_SELECTION);
    } else {
      setSelected(new Set(items.map((s) => s.id)));
    }
  };

  const rowActions = (s: MergeSuggestion) => {
    const name = s.source_company?.name ?? "deleted company";
    if (isMerge) {
      const target = s.target_company?.name ?? "deleted company";
      return (
        <RowActions
          label={name}
          primary={{
            label: "Merge companies",
            icon: <Merge fontSize="small" />,
            disabled: approveMutation.isPending,
            onClick: () => approveMutation.mutate(s.id),
          }}
          actions={[
            {
              label: "Not a duplicate",
              icon: <X fontSize="small" />,
              destructive: true,
              onClick: () => rejectMutation.mutateAsync(s.id),
              confirm: {
                title: `Not a duplicate: ${name}`,
                body: `${name} and ${target} stay separate companies, and the suggestion moves to Rejected.`,
                confirmLabel: "Reject suggestion",
              },
            },
          ]}
        />
      );
    }
    return (
      <RowActions
        label={name}
        primary={{
          label: "Confirm bad merge, then split it on the company page",
          icon: <Split fontSize="small" />,
          disabled: approveMutation.isPending,
          onClick: () => approveMutation.mutate(s.id),
        }}
        actions={[
          {
            label: "Open company to split",
            icon: <ExternalLink fontSize="small" />,
            hidden: !s.source_company,
            onClick: () => window.open(tenantPath(`/companies/${s.source_company!.id}`), "_blank", "noopener"),
          },
          {
            label: "Merge is correct",
            icon: <X fontSize="small" />,
            destructive: true,
            onClick: () => rejectMutation.mutateAsync(s.id),
            confirm: {
              title: `Merge is correct: ${name}`,
              body: "The company stays as it is, and the suggestion moves to Rejected.",
              confirmLabel: "Reject suggestion",
            },
          },
        ]}
      />
    );
  };

  return (
    <Box>
      <PageHeader
        title="Merge review"
        description={`${
          isMerge
            ? "Companies that look like duplicates of each other"
            : "Merged companies whose records may belong to more than one company"
        }${stats ? ` · ${formatNumber(pendingCount)} pending` : ""}`}
      />
      <Tabs
        value={status}
        onChange={(_, v: Status) => url.changeFilter({ status: v === "pending" ? null : v })}
        sx={{ mb: 2 }}
      >
        <Tab
          value="pending"
          label={
            <Badge badgeContent={pendingCount} color="warning" max={999}>
              <Box sx={{ pr: 1.5 }}>Pending</Box>
            </Badge>
          }
        />
        <Tab value="approved" label={`Approved (${formatNumber(approvedCount)})`} />
        <Tab value="rejected" label={`Rejected (${formatNumber(rejectedCount)})`} />
      </Tabs>
      <TableCard
        loading={listQuery.isLoading}
        toolbar={
          <FilterBar
            filters={filters}
            leading={
              <ScopeToggle
                aria-label="Suggestion type"
                value={suggestionType}
                onChange={(v) => url.changeFilter({ type: v === "merge" ? null : v, status: null })}
                options={[
                  { value: "merge", label: "Merge", badge: stats?.merge_pending },
                  { value: "unmerge", label: "Unmerge", badge: stats?.unmerge_pending },
                ]}
              />
            }
            count={listQuery.data && `${formatNumber(total)} ${total === 1 ? "suggestion" : "suggestions"}`}
          />
        }
        footer={total > 0 && <UrlTablePagination total={total} paging={paging} />}
      >
        <ScrollTable>
          <TableHead>
            <TableRow>
              {isPending && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    disabled={items.length === 0}
                    onChange={toggleSelectAll}
                    slotProps={{ input: { "aria-label": "Select all" } }}
                  />
                </TableCell>
              )}
              {isMerge ? (
                <>
                  <TableCell>Source company</TableCell>
                  <TableCell>Target company</TableCell>
                </>
              ) : (
                <>
                  <TableCell>Company</TableCell>
                  <TableCell>Suspect records</TableCell>
                </>
              )}
              <TableCell align="center">Similarity</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Created</TableCell>
              {isPending && <TableCell padding="checkbox" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} hover selected={selected.has(s.id)}>
                {isPending && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(s.id)}
                      onChange={() => toggleSelect(s.id)}
                      slotProps={{ input: { "aria-label": `Select ${s.source_company?.name ?? "suggestion"}` } }}
                    />
                  </TableCell>
                )}
                {isMerge ? (
                  <>
                    <TableCell>
                      <CompanyCell company={s.source_company} />
                    </TableCell>
                    <TableCell>
                      <CompanyCell company={s.target_company} />
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>
                      <CompanyCell company={s.source_company} />
                    </TableCell>
                    <TableCell>
                      <SuspectRecords context={s.context} />
                    </TableCell>
                  </>
                )}
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={`${(s.similarity_score * 100).toFixed(0)}%`}
                    color={similarityColor(s.similarity_score)}
                  />
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={humanize(s.match_method)} />
                </TableCell>
                <TableCell>
                  <FormattedDate value={s.created_at} />
                </TableCell>
                {isPending && (
                  <TableCell padding="checkbox" align="right">
                    {rowActions(s)}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {items.length === 0 && (
              <EmptyTableRow colSpan={colSpan}>
                {listQuery.isError
                  ? "Couldn't load suggestions"
                  : `No ${status} ${suggestionType} suggestions`}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>
      {selected.size > 0 && (
        <BulkActionBar count={selected.size} onClear={() => setSelected(NO_SELECTION)} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={isMerge ? <Merge /> : <Split />}
            disabled={bulkPending}
            loading={bulkApproveMutation.isPending}
            onClick={() => bulkApproveMutation.mutate(Array.from(selected))}
          >
            {isMerge ? "Merge all" : "Confirm all as bad merges"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<X />}
            disabled={bulkPending}
            onClick={() => setConfirmBulkReject(true)}
          >
            Reject all
          </Button>
        </BulkActionBar>
      )}
      <ConfirmDialog
        open={confirmBulkReject}
        title={`Reject ${formatNumber(selected.size)} ${selected.size === 1 ? "suggestion" : "suggestions"}?`}
        confirmLabel="Reject all"
        pending={bulkRejectMutation.isPending}
        onConfirm={() => bulkRejectMutation.mutate(Array.from(selected))}
        onClose={() => setConfirmBulkReject(false)}
      >
        {isMerge
          ? "The companies in them stay separate, and the suggestions move to Rejected."
          : "The companies stay as they are, and the suggestions move to Rejected."}
      </ConfirmDialog>
    </Box>
  );
}
